import type {
  ResidentDefaultFormValues,
  ResidentDetailResponse,
  ResidentDocumentGroup,
  ResidentUploadedDocumentRow,
} from '../types'

/** API file-type tokens for `/serve-file-claims/{fileType}/{attachmentId}` (Angular parity). */
export const RESIDENT_CLAIMS_FILE_TYPES: Record<string, string> = {
  lease_agreement: 'LEASE_AGREEMENT',
  LEASE_AGREEMENT: 'LEASE_AGREEMENT',
  payment_ledger: 'PAYMENT_LEDGER',
  PAYMENT_LEDGER: 'PAYMENT_LEDGER',
  unlawful_detainer: 'PAYMENT_LEDGER',
  UNLAWFUL_DETAINER: 'PAYMENT_LEDGER',
  udfile: 'UDFILE',
  UDFILE: 'UDFILE',
  skip_claim_affidavit: 'SKIP_CLAIM_AFFIDAVIT',
  SKIP_CLAIM_AFFIDAVIT: 'SKIP_CLAIM_AFFIDAVIT',
  cdc_affidavit: 'CDC_AFFIDAVIT',
  CDC_AFFIDAVIT: 'CDC_AFFIDAVIT',
  jurisdiction_eviction_updates: 'JURISDICTION_EVICTION_UPDATES',
  JURISDICTION_EVICTION_UPDATES: 'JURISDICTION_EVICTION_UPDATES',
  other_supporting_documents: 'OTHER_SUPPORTING_DOCUMENTS',
  OTHER_SUPPORTING_DOCUMENTS: 'OTHER_SUPPORTING_DOCUMENTS',
  late_letter: 'LATE_LETTER',
  LATE_LETTER: 'LATE_LETTER',
  DOCUMENT_HISTORY: 'DOCUMENT_HISTORY',
  document_history: 'DOCUMENT_HISTORY',
  /** Settlement statement downloads (Angular `downloadClaimsFile(..., 'CLAIM_SETTLEMENT_DOC')`). */
  CLAIM_SETTLEMENT_DOC: 'CLAIM_SETTLEMENT_DOC',
}

const DOCUMENT_STATUS_KEYS = [
  'lease_agreement',
  'payment_ledger',
  'unlawful_detainer',
  'skip_claim_affidavit',
] as const

function cleanString(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function normalizeDocumentRow(raw: unknown): ResidentUploadedDocumentRow | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const filepath = cleanString(row.filepath ?? row.file_path ?? row.attachment_path)
  const filename = cleanString(row.filename ?? row.file_name ?? row.name ?? row.attachment_name)
  const attachment_id = row.attachment_id ?? row.id ?? row.file_id
  if (!filepath && !filename) return null
  return {
    attachment_id: attachment_id as string | number | undefined,
    filename: filename || filepath.split('/').pop() || 'document',
    filepath,
    filedate: cleanString(row.filedate ?? row.created_at ?? row.uploaded_at) || undefined,
    action: cleanString(row.action) || undefined,
  }
}

/** Reads `document.lease_agreement` whether API returns an array or keyed object (Angular `keyvalue`). */
export function getResidentDocumentsForType(
  documents: ResidentDocumentGroup | null | undefined,
  fileType: string,
): ResidentUploadedDocumentRow[] {
  if (!documents) return []
  const candidates = [
    fileType,
    fileType.toLowerCase(),
    fileType.toUpperCase(),
  ]
  if (fileType.toLowerCase().includes('skip')) {
    candidates.push('skip_claim_affidavit', 'SKIP_CLAIM_AFFIDAVIT', 'skip_affidavit', 'SKIP_AFFIDAVIT')
  } else if (fileType.toLowerCase().includes('detainer') || fileType.toLowerCase().includes('ud')) {
    candidates.push('unlawful_detainer', 'UNLAWFUL_DETAINER', 'udfile', 'UDFILE')
  }

  let source: unknown = undefined
  for (const key of candidates) {
    if (documents[key] !== undefined && documents[key] !== null) {
      source = documents[key]
      break
    }
  }
  if (!source) return []
  const rows = Array.isArray(source) ? source : Object.values(source)
  return rows
    .map((row) => normalizeDocumentRow(row))
    .filter((row): row is ResidentUploadedDocumentRow => row !== null)
}

/**
 * Derives status label for the badge beside each upload (Angular `setFileStatus`).
 */
export function deriveResidentDocumentStatusLabel(entries: ResidentUploadedDocumentRow[]): string {
  if (!entries.length) return ''
  const last = entries[entries.length - 1]
  if (last.action) {
    return last.action === 'REJECTED' ? 'INCOMPLETE' : last.action
  }
  return 'UPLOADED'
}

/**
 * Document-card theme for the reimbursement Required Documentation cards: a
 * status-colored top bar plus the matching pill badge (RECEIVED → green,
 * UPLOADED/PENDING → amber, INCOMPLETE/REJECTED/MISSING → red, else neutral).
 */
export function getResidentDocumentStatusTheme(status: string): { topBar: string; badge: string } {
  const normalized = String(status ?? '').trim().toLowerCase()
  const badgeBase =
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap'
  switch (normalized) {
    case 'received':
    case 'approved':
    case 'complete':
    case 'completed':
    case 'paid':
      return {
        topBar: 'border-t-emerald-500',
        badge: `${badgeBase} border border-emerald-200 bg-emerald-50 text-emerald-700`,
      }
    case 'uploaded':
    case 'pending':
      return {
        topBar: 'border-t-amber-400',
        badge: `${badgeBase} border border-amber-200 bg-amber-50 text-amber-700`,
      }
    case 'incomplete':
    case 'rejected':
    case 'missing':
    case 'denied':
      return {
        topBar: 'border-t-red-500',
        badge: `${badgeBase} border border-red-200 bg-red-50 text-red-600`,
      }
    default:
      return {
        topBar: 'border-t-slate-200',
        badge: `${badgeBase} border border-slate-200 bg-slate-100 text-slate-600`,
      }
  }
}

export function applyDocumentStatusesToFormValues(
  values: ResidentDefaultFormValues,
  detail?: ResidentDetailResponse | null,
): ResidentDefaultFormValues {
  const documents = detail?.document
  if (!documents) return values

  const next = { ...values }
  for (const fileType of DOCUMENT_STATUS_KEYS) {
    const statusKey = `${fileType}_status` as keyof ResidentDefaultFormValues
    const current = cleanString(next[statusKey])
    const derived = deriveResidentDocumentStatusLabel(getResidentDocumentsForType(documents, fileType))
    if (derived && (!current || current === 'MISSING')) {
      ;(next as Record<string, string>)[statusKey] = derived
    }
  }
  return next
}

export function formatResidentDocumentDate(value?: string | unknown): string {
  if (!value) return ''
  const raw = String(value).trim()
  let date: Date
  if (/^\d{10,13}$/.test(raw)) {
    const numeric = Number(raw)
    date = new Date(raw.length >= 13 ? numeric : numeric * 1000)
  } else {
    date = new Date(raw)
  }
  if (Number.isNaN(date.getTime())) return raw
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export function encodeClaimsFilePath(filepath: string): string {
  // Angular downloadClaimsFile / downloadStatement: strip absolute S3 host before encode.
  const s3Key = filepath.replace(/^https?:\/\/[^/]+\/?/, '')
  return encodeURIComponent(s3Key)
}

export function buildResidentClaimsDownloadPath(
  fileTypeKey: string,
  attachmentId: string | number,
  filepath: string,
): string | null {
  const claimsType = RESIDENT_CLAIMS_FILE_TYPES[fileTypeKey]
  if (!claimsType || !attachmentId || !filepath) return null
  return `/serve-file-claims/${claimsType}/${attachmentId}/?url=${encodeClaimsFilePath(filepath)}`
}

export function normalizeResidentDetailDocuments(
  detail: ResidentDetailResponse,
): ResidentDetailResponse {
  const raw = detail as Record<string, unknown>
  const documentSource = (detail.document ?? raw.documents) as ResidentDocumentGroup | undefined
  if (!documentSource || typeof documentSource !== 'object') return detail

  const document: ResidentDocumentGroup = {}
  for (const [key, value] of Object.entries(documentSource)) {
    if (value === null || value === undefined) continue
    document[key] = value as ResidentDocumentGroup[string]
  }

  return { ...detail, document }
}

export type { ResidentUploadedDocumentRow } from '../types'
